import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft, PlayCircle, FileText, CheckCircle, 
  Circle, Clock, BookOpen, ChevronRight
} from 'lucide-react';

const API = '/api';

export const CoursePlayer = () => {
  const { courseId } = useParams();
  const { token } = useAuth();
  const { language, direction } = useLanguage();
  const [courseData, setCourseData] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      const response = await fetch(`${API}/learner/courses/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCourseData(data);
        // Auto-select first incomplete lesson or first lesson
        if (data.lessons?.length > 0) {
          const incomplete = data.lessons.find(l => l.progress?.status !== 'completed');
          setSelectedLesson(incomplete || data.lessons[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async () => {
    if (!selectedLesson || updating) return;
    
    setUpdating(true);
    try {
      const response = await fetch(
        `${API}/learner/progress?content_id=${selectedLesson.content_id}&status=completed`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Refresh course data
        await fetchCourseData();
        
        // Auto-advance to next lesson
        const currentIndex = courseData.lessons.findIndex(l => l.content_id === selectedLesson.content_id);
        if (currentIndex < courseData.lessons.length - 1) {
          setSelectedLesson(courseData.lessons[currentIndex + 1]);
        }
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    } finally {
      setUpdating(false);
    }
  };

  const getLessonIcon = (lesson) => {
    const status = lesson.progress?.status;
    if (status === 'completed') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (lesson.content_type === 'video') {
      return <PlayCircle className="h-5 w-5 text-[#33AADD]" />;
    }
    return <FileText className="h-5 w-5 text-[#33AADD]" />;
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#111827] mb-4">
            {language === 'ar' ? 'لم يتم العثور على الدورة' : 'Course not found'}
          </h2>
          <Link to="/learner">
            <Button className="btn-primary">
              {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { course, enrollment, lessons } = courseData;
  const courseTitle = language === 'ar' ? course.title_ar : course.title;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="course-player">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/learner" 
                className="text-[#6B7280] hover:text-[#33AADD] transition-colors"
              >
                <ArrowLeft className={`h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
              </Link>
              <div>
                <h1 className="font-semibold text-[#111827] line-clamp-1">{courseTitle}</h1>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#6B7280]">
                    {Math.round(enrollment.progress_percent)}% {language === 'ar' ? 'مكتمل' : 'complete'}
                  </span>
                </div>
              </div>
            </div>
            <Progress value={enrollment.progress_percent} className="w-32 h-2 hidden sm:block" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row">
        {/* Lesson List - Sidebar */}
        <div className="lg:w-80 bg-white border-e border-[#E5E7EB] lg:min-h-[calc(100vh-73px)]">
          <div className="p-4 border-b border-[#E5E7EB]">
            <h2 className="font-semibold text-[#111827]">
              {language === 'ar' ? 'محتوى الدورة' : 'Course Content'}
            </h2>
            <p className="text-sm text-[#6B7280]">
              {lessons.filter(l => l.progress?.status === 'completed').length} / {lessons.length} {language === 'ar' ? 'مكتمل' : 'completed'}
            </p>
          </div>
          
          <div className="overflow-y-auto max-h-[calc(100vh-180px)]">
            {lessons.map((lesson, index) => (
              <button
                key={lesson.content_id}
                onClick={() => setSelectedLesson(lesson)}
                className={`w-full p-4 flex items-start gap-3 text-start border-b border-[#E5E7EB] transition-colors ${
                  selectedLesson?.content_id === lesson.content_id
                    ? 'bg-[#33AADD]/5 border-s-4 border-s-[#33AADD]'
                    : 'hover:bg-[#F9FAFB]'
                }`}
                data-testid={`lesson-${index}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getLessonIcon(lesson)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium line-clamp-2 ${
                    lesson.progress?.status === 'completed' ? 'text-[#6B7280]' : 'text-[#111827]'
                  }`}>
                    {language === 'ar' ? lesson.title_ar : lesson.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-[#9CA3AF]">
                    {lesson.content_type === 'video' && <span>Video</span>}
                    {lesson.content_type === 'pdf' && <span>PDF</span>}
                    {lesson.duration_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {lesson.duration_minutes} min
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className={`h-4 w-4 text-[#9CA3AF] flex-shrink-0 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 lg:p-8">
          {selectedLesson ? (
            <div className="max-w-4xl mx-auto">
              {/* Lesson Header */}
              <div className="mb-6">
                <Badge variant="outline" className="mb-2">
                  {selectedLesson.content_type === 'video' ? 'Video' : 'Document'}
                </Badge>
                <h2 className="text-2xl font-bold text-[#111827] mb-2">
                  {language === 'ar' ? selectedLesson.title_ar : selectedLesson.title}
                </h2>
                {selectedLesson.description && (
                  <p className="text-[#6B7280]">
                    {language === 'ar' ? selectedLesson.description_ar : selectedLesson.description}
                  </p>
                )}
              </div>

              {/* Content */}
              <Card className="mb-6">
                <CardContent className="p-0">
                  {selectedLesson.content_type === 'video' && selectedLesson.content_url ? (
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      <iframe
                        src={getVideoEmbedUrl(selectedLesson.content_url)}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={selectedLesson.title}
                      />
                    </div>
                  ) : selectedLesson.content_type === 'pdf' && selectedLesson.content_url ? (
                    <div className="aspect-[4/3] bg-[#F9FAFB]">
                      <iframe
                        src={selectedLesson.content_url}
                        className="w-full h-full"
                        title={selectedLesson.title}
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-[#F9FAFB] flex items-center justify-center">
                      <div className="text-center text-[#6B7280]">
                        <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                        <p>{language === 'ar' ? 'المحتوى غير متاح' : 'Content not available'}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div>
                  {selectedLesson.progress?.status === 'completed' ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-4 w-4 me-1" />
                      {language === 'ar' ? 'مكتمل' : 'Completed'}
                    </Badge>
                  ) : (
                    <Button 
                      onClick={markComplete}
                      disabled={updating}
                      className="btn-primary"
                      data-testid="mark-complete-btn"
                    >
                      {updating ? (
                        <span className="animate-spin">⏳</span>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'وضع علامة مكتمل' : 'Mark as Complete'}
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {/* Next Lesson Button */}
                {(() => {
                  const currentIndex = lessons.findIndex(l => l.content_id === selectedLesson.content_id);
                  const nextLesson = lessons[currentIndex + 1];
                  if (nextLesson) {
                    return (
                      <Button 
                        variant="outline"
                        onClick={() => setSelectedLesson(nextLesson)}
                      >
                        {language === 'ar' ? 'الدرس التالي' : 'Next Lesson'}
                        <ChevronRight className={`h-4 w-4 ms-1 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                      </Button>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-[#6B7280]">
                <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>{language === 'ar' ? 'اختر درساً للبدء' : 'Select a lesson to begin'}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;
